import { useRef, useState, useEffect, useCallback } from "react";

const STORAGE_KEY = "helprr_audio_muted";
const DEFAULT_VOLUME = 0.25;
const FADE_DURATION_MS = 600;
const FADE_STEPS = 12;

export function useTemplateAudio(trackSrc) {
  const audioRef = useRef(null);
  const fadeIntervalRef = useRef(null);
  const startedRef = useRef(false);
  const isMutedRef = useRef(false);
  const isFadingRef = useRef(false);
  const [isMuted, setIsMuted] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "true"
  );

  // Keep ref in sync — the visibilitychange listener reads from ref to avoid stale state.
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    return () => {
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (!audioRef.current || !startedRef.current) return;
      if (document.hidden) {
        audioRef.current.pause();
      } else if (!isMutedRef.current) {
        audioRef.current.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const start = useCallback(() => {
    if (!trackSrc) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(trackSrc);
      audioRef.current.loop = true;
    }
    startedRef.current = true;
    audioRef.current.volume = DEFAULT_VOLUME;
    if (!isMuted) {
      audioRef.current.play().catch(() => {});
    }
  }, [trackSrc, isMuted]);

  const stop = useCallback(() => {
    if (!audioRef.current) return;
    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    const audio = audioRef.current;
    const startVol = audio.volume || DEFAULT_VOLUME;
    let step = 0;
    isFadingRef.current = true;
    fadeIntervalRef.current = setInterval(() => {
      step++;
      audio.volume = Math.max(0, startVol * (1 - step / FADE_STEPS));
      if (step >= FADE_STEPS) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
        isFadingRef.current = false;
        startedRef.current = false;
        audio.pause();
        audio.volume = DEFAULT_VOLUME; // reset so start() works correctly if called again
      }
    }, FADE_DURATION_MS / FADE_STEPS);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      if (audioRef.current && !isFadingRef.current) {
        if (next) {
          audioRef.current.volume = 0;
        } else {
          audioRef.current.volume = DEFAULT_VOLUME;
          if (startedRef.current && !document.hidden) {
            audioRef.current.play().catch(() => {});
          }
        }
      }
      return next;
    });
  }, []);

  return { isMuted, toggleMute, start, stop };
}
